import tokens from '@kluein/design-tokens';
import { Box, Button } from '@kluein/klue-ui';
import { ArrowDown, ArrowUp } from '@phosphor-icons/react';
import { AnimatePresence, motion } from 'framer-motion';
import { forwardRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import useScrollObserver from 'hooks/use-scroll-observer/useScrollObserver';
import TEST_IDS from 'test-ids';

import type { FadeProps, ScrollerProps } from './Scroller.types';

const FloatingButton = styled(Button).attrs({
  alignSelf: 'center',
  style: {
    position: 'absolute',
    zIndex: 1,
    bottom: 0,
  },
  margin: {
    bottom: 'xlarge',
  },
})`
  height: 40px;
  > div {
    height: 100%;
  }

  background-color: ${tokens.color.highlight.light};
  border-color: ${tokens.color.highlight.light};
  border-radius: 16.5px;
  color: ${tokens.color.highlight.contrastlight};
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.2);

  &:hover {
    background-color: ${tokens.color.highlight.light};
    border-color: ${tokens.color.highlight.light};
    color: ${tokens.color.highlight.contrastlight};
    box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.15), 0px 4px 8px rgba(0, 0, 0, 0.2);
  }

  &:active {
    background-color: ${tokens.color.highlight.light};
    border-color: ${tokens.color.highlight.light};
    color: ${tokens.color.highlight.contrastlight};
    box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.15), 0px 4px 8px rgba(0, 0, 0, 0.2);
  }
`;

const FadeBox = motion(styled(Box)<FadeProps>`
  position: absolute;
  left: 0;
  width: 100%;
  ${({
    currentBackgroundColor = tokens.color.neutral.white.main,
    position,
  }) => {
    if (position === 'top') {
      return `
        top: 0;
        background: linear-gradient(${currentBackgroundColor}, transparent);
      `;
    }
    return `
      bottom: 0;
      background: linear-gradient(transparent, ${currentBackgroundColor});
    `;
  }}
  pointer-events: none;
`);

const Fade = ({ height = 'xxsmall', ...props }: FadeProps) => (
  <AnimatePresence>
    {props.isVisible && (
      <FadeBox
        variants={{
          visible: { opacity: 1 },
          gone: { opacity: 0 },
        }}
        initial="visible"
        animate="visible"
        exit="gone"
        height={height}
        {...props}
      />
    )}
  </AnimatePresence>
);

export const Scroller = forwardRef(
  (
    {
      label,
      children,
      showMoreButton = false,
      showBackToTopButton = false,
      disableFade = false,
      padScrollbar = true,
      fadeProps: { currentBackgroundColor, height: fadeHeight } = {},
      style,
      ...rest
    }: Omit<ScrollerProps, 'ref'>,
    ref?: React.ForwardedRef<HTMLElement>,
  ) => {
    const { t } = useTranslation();
    const {
      scrollState: { isTop, isBottom, isOverflowedY, isScrolledY },
      refs: { observeScroll, observedNode },
    } = useScrollObserver(ref);

    const onClickShowMore = useCallback(
      (event: React.MouseEvent<HTMLElement>) => {
        const { currentTarget } = event;
        if (observedNode.current) {
          const node = observedNode.current as HTMLElement;
          node.scrollTop = isBottom ? 0 : node.scrollHeight - node.offsetHeight;
        }
        currentTarget.blur();
      },
      [isBottom, observedNode],
    );

    const onClickBackToTop = useCallback(
      (event: React.MouseEvent<HTMLElement>) => {
        const { currentTarget } = event;
        if (observedNode.current) {
          (observedNode.current as HTMLElement).scrollTop = 0;
        }
        currentTarget.blur();
      },
      [observedNode],
    );

    const isFadeHeightObj = typeof fadeHeight === 'object';
    const topFadeHeight = isFadeHeightObj ? fadeHeight.top : fadeHeight;
    const bottomFadeHeight = isFadeHeightObj ? fadeHeight.bottom : fadeHeight;

    return (
      <Box
        data-test-id={TEST_IDS.layout.scroller.container}
        style={{
          position: 'relative',
          clipPath: 'inset(0%)',
          ...style,
        }}
      >
        <Box
          data-test-id={TEST_IDS.layout.scroller.scroll}
          responsive={false}
          ref={observeScroll}
          overflow={{ vertical: 'auto' }}
          pad={{
            bottom:
              showMoreButton || showBackToTopButton ? 'xxlarge' : undefined,
            ...(padScrollbar ? { end: 'medium' } : {}),
          }}
          {...rest}
        >
          {children}
          {showMoreButton && isOverflowedY && (
            <Box flex={false} height="xsmall" />
          )}
        </Box>
        {showMoreButton && isOverflowedY && (
          <Box data-test-id={TEST_IDS.layout.scroller.moreTip}>
            <FloatingButton
              label={
                isBottom
                  ? t('Common:scroller.backToTop')
                  : label || t('Common:scroller.more')
              }
              leftSlot={
                isBottom ? (
                  <ArrowUp color="currentColor" />
                ) : (
                  <ArrowDown color="currentColor" />
                )
              }
              onClick={onClickShowMore}
            />
          </Box>
        )}
        {showBackToTopButton && isScrolledY && (
          <Box data-test-id={TEST_IDS.layout.scroller.backToTop}>
            <FloatingButton
              label={t('Common:scroller.backToTop')}
              leftSlot={<ArrowUp color="currentColor" />}
              onClick={onClickBackToTop}
            />
          </Box>
        )}
        {!disableFade && (
          <>
            <Fade
              data-test-id={TEST_IDS.layout.scroller.fade.top}
              position="top"
              isVisible={isOverflowedY && !isTop}
              currentBackgroundColor={currentBackgroundColor}
              height={topFadeHeight}
            />
            <Fade
              data-test-id={TEST_IDS.layout.scroller.fade.bottom}
              position="bottom"
              isVisible={isOverflowedY && !isBottom}
              currentBackgroundColor={currentBackgroundColor}
              height={bottomFadeHeight}
            />
          </>
        )}
      </Box>
    );
  },
);

export default Scroller;
